// Маршрутный лист учёта разъездов, форма ФРУ № 12 — для бухгалтерии
// (ADR-008 §9, issue #510).
//
// ПЕЧАТЬ И EXCEL — ИЗ ОДНОГО СПИСКА ГРАФ. Графы, их порядок и текст ячеек
// живут здесь; экран с печатью (`RouteSheetFormSheet.tsx`) и таблица Excel
// читают одно и то же. Разойдись они — бухгалтерия считала бы по одной
// таблице, а подписанной бумагой была бы другая.
//
// ДАННЫЕ — С СЕРВЕРА (`GET /rail/route-sheets/:id/form`): утверждённый бланк —
// из подписанных байтов, панель ничего не досчитывает.
//
// НЕУТВЕРЖДЁННЫЙ ЛИСТ (решение владельца по PR #593) печатается и выгружается
// с пометкой «Не утверждён к оплате» (`approvedForPayment: false`): в шапке и на
// каждой странице печати, в Excel — строкой в шапке листа. Утверждённый — как
// прежде, без пометки, с реквизитами подписи.
//
// ФИО — только из карточки. Пустое — «ФИО не заполнено»; профессию на его
// место не подставляем (находка стенда 30.09.2026).

import type { RouteSheetForm, RouteSheetFormLine, RouteSheetFormMark, RouteSheetFormSignature } from '@railroute/contracts/rail';

export const FORM_TITLE = 'Маршрутный лист учёта разъездов';
export const FORM_CODE = 'Форма ФРУ № 12';
export const FIO_MISSING = 'ФИО не заполнено';
/** Пометка неутверждённого листа — в печати и в Excel одними словами. */
export const UNAPPROVED_MARK = 'Не утверждён к оплате';
/** «Утверждаю к оплате» неутверждённого листа. */
export const NOT_APPROVED = 'не утверждено';
/** Документ и хеш листа, который ещё не сдан: экземпляра нет. */
export const NOT_SUBMITTED = 'лист не сдан — экземпляра нет';

/**
 * Графы таблицы переездов в порядке бланка ФРУ № 12. «Отметка» стоит там, где
 * на бумаге расписывался руководитель в строке: электронный лист ставит туда
 * отметку по QR со временем, ручную — с пометкой «ручная, подтвердил …».
 */
export const FORM_COLUMNS = [
  '№ п/п',
  'Выбыл: дата',
  'Выбыл: время',
  'Выбыл: № поезда',
  'Выбыл: станция (код)',
  'Прибыл: дата',
  'Прибыл: время',
  'Прибыл: № поезда',
  'Прибыл: станция (код)',
  'Наименование работ',
  'Примечание',
  'Отметка вместо подписи руководителя',
] as const;

/** Двухуровневая шапка печатной таблицы — те же графы, сгруппированные как на бланке. */
export const FORM_COLUMN_GROUPS = [
  { title: '№ п/п', span: 1 },
  { title: 'Выбыл', span: 4 },
  { title: 'Прибыл', span: 4 },
  { title: 'Наименование работ', span: 1 },
  { title: 'Примечание', span: 1 },
  { title: 'Отметка', span: 1 },
] as const;
export const FORM_SUBCOLUMNS = ['', 'дата', 'время', '№ поезда', 'станция', 'дата', 'время', '№ поезда', 'станция', '', '', ''] as const;

/** `YYYY-MM-DD` → `ДД.ММ.ГГГГ`, без часового пояса браузера. */
export function formDate(day: string): string {
  const [y, m, d] = day.split('-');
  return y && m && d ? `${d}.${m}.${y}` : day;
}

/** `YYYY-MM` → «август 2026». */
const MONTHS = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];
export function formMonth(month: string): string {
  const [y, m] = month.split('-');
  const name = MONTHS[Number(m) - 1];
  return name ? `${name} ${y}` : month;
}

/** Станция с кодом ЕСР — так её узнаёт бухгалтерия (ADR-008 §5). */
export function formStation(mark: RouteSheetFormMark): string {
  if (!mark.stationName) return '—';
  return mark.stationEsrCode ? `${mark.stationName} (${mark.stationEsrCode})` : mark.stationName;
}

/** Момент подписи по Казахстану (UTC+5, без перехода на летнее время). */
export function formInstant(iso: string): string {
  const at = new Date(Date.parse(iso) + 5 * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getUTCDate())}.${pad(at.getUTCMonth() + 1)}.${at.getUTCFullYear()} ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
}

/** Графа подписи: кто, когда и чем. */
export function formSignature(signature: RouteSheetFormSignature | null): string {
  if (!signature) return 'не подписано';
  const how = signature.level === 'QUALIFIED_ECP' ? 'ЭЦП' : 'ключ телефона';
  return `${signature.name}, таб. ${signature.employeeNumber} — ${formInstant(signature.signedAt)}, ${how}`;
}

function markNote(mark: RouteSheetFormMark): string {
  if (mark.method === 'MANUAL_FALLBACK') {
    return mark.manualApprovedByName
      ? `ручная, подтвердил ${mark.manualApprovedByName}, таб. ${mark.manualApprovedByEmployeeNumber ?? '—'}`
      : 'ручная, не подтверждена';
  }
  return `QR ${mark.time}`;
}

/** Графа «Отметка»: по выбытию и прибытию, ручная — помечена. */
export function formMarkCell(line: RouteSheetFormLine): string {
  return `выбыл: ${markNote(line.departed)}; прибыл: ${markNote(line.arrived)}`;
}

const RESULT_TEXT = { DONE: 'выполнено', PARTIAL: 'выполнено частично', NOT_DONE: 'не выполнено' } as const;

/** Ячейки строки — по графам `FORM_COLUMNS`. */
export function formCells(line: RouteSheetFormLine): string[] {
  const note = line.resultStatus ? RESULT_TEXT[line.resultStatus] : '';
  return [
    String(line.sequence),
    formDate(line.departed.date),
    line.departed.time,
    line.departed.trainNumber ?? '—',
    formStation(line.departed),
    formDate(line.arrived.date),
    line.arrived.time,
    line.arrived.trainNumber ?? '—',
    formStation(line.arrived),
    line.returnToHome ? 'возвращение к месту приписки' : (line.workName ?? '—'),
    [note, line.resultComment].filter(Boolean).join(': '),
    formMarkCell(line),
  ];
}

/** Шапка бланка: подпись-гриф и реквизиты листа, парами «графа — значение». */
export function formHeader(form: RouteSheetForm): [string, string][] {
  return [
    ['Утверждаю к оплате', form.approved ? formSignature(form.approved) : NOT_APPROVED],
    ['Маршрутный лист №', form.number ?? '—'],
    ['Организация', form.subdivisionName ? `${form.organizationName}, ${form.subdivisionName}` : form.organizationName],
    ['Должность', form.position ?? '—'],
    ['Ф. И. О.', form.workerFullName ?? FIO_MISSING],
    ['Табельный №', form.workerEmployeeNumber],
    ['Месяц', formMonth(form.month)],
    [
      'Место приписки',
      form.homeStationName
        ? form.homeStationEsrCode
          ? `ст. ${form.homeStationName}, код ${form.homeStationEsrCode}`
          : `ст. ${form.homeStationName}`
        : '—',
    ],
  ];
}

/** Низ бланка: выдача, подписи работника и старшего, реквизиты документа. */
export function formFooter(form: RouteSheetForm): [string, string][] {
  return [
    ['Выдал начальник цеха', formSignature(form.issued)],
    ['Сдал (работник)', formSignature(form.submitted)],
    ['Подтверждаю (старший)', formSignature(form.confirmed)],
    ['Документ', form.documentId ?? NOT_SUBMITTED],
    ['Хеш документа', form.hash ?? NOT_SUBMITTED],
  ];
}

/** Лист Excel: шапка, таблица по графам бланка, низ — те же тексты, что в печати. */
export function formSheet(form: RouteSheetForm): string[][] {
  return [
    [`${FORM_TITLE} — ${FORM_CODE}`],
    ...(form.approvedForPayment ? [] : [[UNAPPROVED_MARK.toUpperCase()]]),
    ...formHeader(form).map(([label, value]) => [label, value]),
    [],
    [...FORM_COLUMNS],
    ...form.lines.map(formCells),
    [],
    ...formFooter(form).map(([label, value]) => [label, value]),
  ];
}

/** Имя файла выгрузки: номер листа, табельный и месяц. */
export function formFileName(form: RouteSheetForm, extension: 'xlsx'): string {
  const number = (form.number ?? 'без номера').replace(/[\\/:*?"<>|]/g, '-');
  return `ФРУ-12 ${number} таб. ${form.workerEmployeeNumber} ${form.month}.${extension}`;
}
